import React, { useState, useEffect, useRef } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import { eduStatus } from "../../utilis/appStatus";

function ClientSignupData() {
  // intake
  const [intakes, setIntakes] = useState([]);
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [selectedIntakeId, setSelectedIntakeId] = useState("");
  const [intakeSearch, setIntakeSearch] = useState("");

  // provider
  const [isProviderOpen, setIsProviderOpen] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [providerSearch, setProviderSearch] = useState("");
  const [providersList, setProvidersList] = useState([]);

  // course / package
  const [isCourseOpen, setIsCourseOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [coursesList, setCoursesList] = useState([]);

  // filter / pagination
  const [filterKey, setFilterKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });
  const [result, setResult] = useState([]);
  const [students, setStudents] = useState(null);

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [statusSearch, setStatusSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const filteredStatuses = React.useMemo(() => {
    return eduStatus
      .filter((status) =>
        status.title.toLowerCase().includes(statusSearch.toLowerCase()),
      )
      .reduce((unique, item) => {
        if (!unique.some((obj) => obj.title === item.title)) {
          unique.push(item);
        }
        return unique;
      }, []);
  }, [statusSearch]);

  // ref for intake , provider , course dropdown to handle outside click
  const intakeRef = useRef(null);
  const providerRef = useRef(null);
  const courseRef = useRef(null);
  const statusRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (intakeRef.current && !intakeRef.current.contains(event.target)) {
        setIsIntakeOpen(false);
      }
      if (providerRef.current && !providerRef.current.contains(event.target)) {
        setIsProviderOpen(false);
      }
      if (courseRef.current && !courseRef.current.contains(event.target)) {
        setIsCourseOpen(false);
      }
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setIsStatusOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchIntakes();
    fetchProviders();
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [selectedProviderId]);

  const applyFilters = () => {
    setFilterKey((prev) => prev + 1);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const clearFilters = () => {
    setFilterKey((prev) => prev + 1);

    setSelectedProviderId("");
    setSelectedCourseId("");

    setProviderSearch("");
    setCourseSearch("");

    setIsIntakeOpen(false);
    setIsProviderOpen(false);
    setIsCourseOpen(false);
    setSelectedStatuses([]);

    setStatusSearch("");
    setIsStatusOpen(false);

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  const handlePerPageChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value),
      currentPage: 1,
    }));
  };

  const fetchCourses = async () => {
    if (!selectedProviderId) {
      setCoursesList([]);
      setSelectedCourseId("");
      return;
    }
    try {
      const response = await apiUrl.get(
        `/getCourseByNames/${selectedProviderId}`,
      );
      if (response?.data?.status) {
        setCoursesList(
          Array.isArray(response?.data?.data) ? response?.data?.data : [],
        );
      } else {
        setCoursesList([]);
      }
    } catch (error) {
      console.error(error);
      setCoursesList([]);
    }
  };

  const fetchIntakes = async () => {
    try {
      const response = await apiUrl.get("/fetchIntakes");
      if (response?.data?.status && Array.isArray(response?.data?.data)) {
        const intakesData = response?.data?.data;
        setIntakes(intakesData);

        if (intakesData.length > 0 && !selectedIntakeId) {
          setSelectedIntakeId(intakesData[0].id);
        }
      }
    } catch (error) {
      console.log("Error fetching intakes:", error);
    }
  };

  const fetchProviders = async () => {
    try {
      let selectedCategory = 8;
      const response = await apiUrl(`/getProvidersByNames/${selectedCategory}`);
      if (response?.data?.status) {
        setProvidersList(
          Array.isArray(response?.data?.data) ? response?.data?.data : [],
        );
      }
    } catch (error) {
      console.log("Error fetching providers:", error);
    }
  };

  const findApplications = async () => {
    if (!selectedIntakeId) return;
    try {
      setLoading(true);

      const statusIds =
        selectedStatuses.length > 0
          ? selectedStatuses.map((status) => status.value)
          : undefined;

      const response = await apiUrl.get(`/getClientsDataWithIntake`, {
        params: {
          page: pagination.currentPage,
          perPage: pagination.perPage,
          providerId: selectedProviderId || undefined,
          courseId: selectedCourseId || undefined,
          intakeId: selectedIntakeId || undefined,
          statusIds: statusIds,
        },
      });
      if (response?.data?.status === true) {
        setResult(response?.data?.data || []);
        setStudents(response?.data?.intake_summary?.total_students || 0);
        setPagination((prev) => ({
          ...prev,
          total: response?.data?.pagination?.total || 0,
          lastPage: response?.data?.pagination?.lastPage || 1,
        }));
      } else {
        setResult([]);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedIntakeId || selectedProviderId || selectedCourseId) {
      findApplications();
    }
  }, [pagination.currentPage, pagination.perPage, filterKey, selectedIntakeId]);

  const filteredIntakes = React.useMemo(() => {
    if (!intakeSearch.trim()) return intakes;
    return intakes.filter((intake) =>
      intake.title.toLowerCase().includes(intakeSearch.toLowerCase()),
    );
  }, [intakes, intakeSearch]);

  const filteredProviders = React.useMemo(() => {
    if (!providerSearch.trim()) return providersList;
    return providersList.filter((provider) =>
      provider.name.toLowerCase().includes(providerSearch.toLowerCase()),
    );
  }, [providersList, providerSearch]);

  const filteredCourses = React.useMemo(() => {
    if (!courseSearch.trim()) return coursesList;
    return coursesList.filter((course) =>
      course.name.toLowerCase().includes(courseSearch.toLowerCase()),
    );
  }, [coursesList, courseSearch]);
  return (
    <>
      <div className="card px-4 py-3 bg-white">
        {/* header */}
        <header className="mb-3">
          <div className="container px-4">
            <div className="d-flex align-items-center justify-content-between">
              {/* Left side */}
              <div>
                <h1 className="h3 fw-bold mb-1">
                  Intake-wise Application Status Report
                </h1>
                <p className="text-muted small mb-0">
                  Track student applications by intake — view current status,
                  milestone dates, and progress at a glance.
                </p>
              </div>

              {/* Right side bigger card */}
              <div className="card shadow-sm border-0">
                <div className="card-body d-flex align-items-center gap-4 py-3 px-4">
                  <div>
                    <h6 className="text-muted mb-1">
                      Total Students in This Intake
                    </h6>
                    <h2 className="fw-bold mb-0 text-primary">{students}</h2>
                  </div>

                  <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                    <i className="bi bi-people-fill text-primary fs-3"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* filter section */}
        {/* <div className="mb-4 bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div
            className="d-flex flex-wrap align-items-end gap-3"
            style={{ marginBottom: "1rem" }}
          >
            <div className="flex-1 min-w-[120px] relative intake-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Intake
              </label>
              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white focus-within:ring-1 focus-within:ring-blue-500"
                onClick={() => setIsIntakeOpen(!isIntakeOpen)}
              >
                <span className="truncate text-gray-800 block overflow-hidden text-ellipsis">
                  {selectedIntakeId
                    ? intakes?.find((p) => p.id == selectedIntakeId)?.title ||
                      "— Select —"
                    : "— Select —"}
                </span>
              </div>

              {isIntakeOpen && (
                <div
                  ref={intakeRef}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search intakes..."
                      value={intakeSearch}
                      onChange={(e) => setIntakeSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div
                    className="max-h-52 overflow-y-auto"
                    style={{ maxHeight: "400px" }}
                  >
                    {filteredIntakes?.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No Intake
                      </div>
                    ) : (
                      filteredIntakes?.map((intake) => (
                        <div
                          key={intake.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIntakeId(intake.id);
                            setIsIntakeOpen(false);
                            setIntakeSearch("");
                          }}
                        >
                          {intake.title}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 min-w-[180px] relative provider-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Provider
              </label>
              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white focus-within:ring-1 focus-within:ring-blue-500"
                onClick={() => setIsProviderOpen(!isProviderOpen)}
              >
                <span className="truncate text-gray-800 block overflow-hidden text-ellipsis">
                  {selectedProviderId
                    ? providersList.find((p) => p.id == selectedProviderId)
                        ?.name || "— Select —"
                    : "— Select —"}
                </span>
              </div>

              {isProviderOpen && (
                <div
                  ref={providerRef}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search providers..."
                      value={providerSearch}
                      onChange={(e) => setProviderSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div
                    className="max-h-52 overflow-y-auto"
                    style={{ maxHeight: "400px" }}
                  >
                    {filteredProviders?.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No providers found
                      </div>
                    ) : (
                      filteredProviders?.map((provider) => (
                        <div
                          key={provider.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProviderId(provider.id);
                            setIsProviderOpen(false);
                            setProviderSearch("");
                          }}
                        >
                          {provider.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 min-w-[180px] relative course-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Course/Package
              </label>
              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white focus-within:ring-1 focus-within:ring-blue-500"
                onClick={() => setIsCourseOpen(!isCourseOpen)}
              >
                <span className="truncate text-gray-800 block overflow-hidden text-ellipsis">
                  {selectedCourseId
                    ? coursesList?.find((p) => p.id == selectedCourseId)
                        ?.name || "— Select —"
                    : "— Select —"}
                </span>
              </div>

              {isCourseOpen && (
                <div
                  ref={courseRef}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search courses..."
                      value={courseSearch}
                      onChange={(e) => setCourseSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div
                    className="max-h-52 overflow-y-auto"
                    style={{ maxHeight: "400px" }}
                  >
                    {filteredCourses?.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No Course/Packge found
                      </div>
                    ) : (
                      filteredCourses?.map((course) => (
                        <div
                          key={course.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCourseId(course.id);
                            setIsCourseOpen(false);
                            setCourseSearch("");
                          }}
                        >
                          {course.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            className="d-flex  align-items-center gap-4"
            style={{ marginBottom: "1rem" }}
          >
            <div className="flex-1 min-w-[200px] relative status-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Status
              </label>

              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white"
                onClick={() => setIsStatusOpen(!isStatusOpen)}
              >
                <span className="text-gray-800">
                  {selectedStatuses.length > 0
                    ? `${selectedStatuses.length} selected`
                    : "— Select —"}
                </span>
              </div>

              {isStatusOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search status..."
                      value={statusSearch}
                      onChange={(e) => setStatusSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded outline-none"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div className="max-h-52 overflow-y-auto">
                    {filteredStatuses.map((status) => {
                      const isSelected = selectedStatuses.some(
                        (s) => s.value === status.value,
                      );

                      return (
                        <div
                          key={status.value}
                          className={`px-3 py-2 cursor-pointer text-sm ${
                            isSelected ? "bg-blue-100" : "hover:bg-gray-100"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();

                            if (isSelected) {
                              setSelectedStatuses(
                                selectedStatuses.filter(
                                  (s) => s.value !== status.value,
                                ),
                              );
                            } else {
                              setSelectedStatuses([
                                ...selectedStatuses,
                                status,
                              ]);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="me-2"
                          />
                          {status.title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {selectedStatuses.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {selectedStatuses.map((status) => (
                  <div
                    key={status.value}
                    className="badge bg-primary d-flex align-items-center px-3 py-2"
                  >
                    <span className="me-2">{status.title}</span>
                    <button
                      className="btn-close btn-close-white btn-sm"
                      onClick={() =>
                        setSelectedStatuses(
                          selectedStatuses.filter(
                            (s) => s.value !== status.value,
                          ),
                        )
                      }
                    ></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button
              onClick={clearFilters}
              className="btn btn-light"
              style={{ border: "1px solid #d1d5db", fontWeight: 500 }}
            >
              Clear All
            </button>
            <button
              onClick={applyFilters}
              className="btn btn-primary"
              style={{ fontWeight: 500 }}
            >
              Apply Filters
            </button>
          </div>
        </div> */}
        <div className="mb-4 bg-gray-50 rounded-lg border border-gray-200 p-4">
          {/* Row 1: Intake, Provider, Course */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            {/* intake drop down */}
            <div className="flex-1 min-w-[120px] relative intake-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Intake
              </label>
              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white focus-within:ring-1 focus-within:ring-blue-500"
                onClick={() => setIsIntakeOpen(!isIntakeOpen)}
              >
                <span className="truncate text-gray-800 block overflow-hidden text-ellipsis">
                  {selectedIntakeId
                    ? intakes?.find((p) => p.id == selectedIntakeId)?.title ||
                      "— Select —"
                    : "— Select —"}
                </span>
              </div>

              {isIntakeOpen && (
                <div
                  ref={intakeRef}
                  className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search intakes..."
                      value={intakeSearch}
                      onChange={(e) => setIntakeSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div
                    className="max-h-52 overflow-y-auto"
                    style={{ maxHeight: "400px" }}
                  >
                    {filteredIntakes?.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No Intake
                      </div>
                    ) : (
                      filteredIntakes?.map((intake) => (
                        <div
                          key={intake.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIntakeId(intake.id);
                            setIsIntakeOpen(false);
                            setIntakeSearch("");
                          }}
                        >
                          {intake.title}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* provider drop down */}
            <div className="flex-1 min-w-[180px] relative provider-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Provider
              </label>
              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white focus-within:ring-1 focus-within:ring-blue-500"
                onClick={() => setIsProviderOpen(!isProviderOpen)}
              >
                <span className="truncate text-gray-800 block overflow-hidden text-ellipsis">
                  {selectedProviderId
                    ? providersList.find((p) => p.id == selectedProviderId)
                        ?.name || "— Select —"
                    : "— Select —"}
                </span>
              </div>

              {isProviderOpen && (
                <div
                  ref={providerRef}
                  className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search providers..."
                      value={providerSearch}
                      onChange={(e) => setProviderSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div
                    className="max-h-52 overflow-y-auto"
                    style={{ maxHeight: "400px" }}
                  >
                    {filteredProviders?.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No providers found
                      </div>
                    ) : (
                      filteredProviders?.map((provider) => (
                        <div
                          key={provider.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProviderId(provider.id);
                            setIsProviderOpen(false);
                            setProviderSearch("");
                          }}
                        >
                          {provider.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* course / provider */}
            <div className="flex-1 min-w-[180px] relative course-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Course/Package
              </label>
              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white focus-within:ring-1 focus-within:ring-blue-500"
                onClick={() => setIsCourseOpen(!isCourseOpen)}
              >
                <span className="truncate text-gray-800 block overflow-hidden text-ellipsis">
                  {selectedCourseId
                    ? coursesList?.find((p) => p.id == selectedCourseId)
                        ?.name || "— Select —"
                    : "— Select —"}
                </span>
              </div>

              {isCourseOpen && (
                <div
                  ref={courseRef}
                  className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search courses..."
                      value={courseSearch}
                      onChange={(e) => setCourseSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div
                    className="max-h-52 overflow-y-auto"
                    style={{ maxHeight: "400px" }}
                  >
                    {filteredCourses?.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No Course/Packge found
                      </div>
                    ) : (
                      filteredCourses?.map((course) => (
                        <div
                          key={course.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCourseId(course.id);
                            setIsCourseOpen(false);
                            setCourseSearch("");
                          }}
                        >
                          {course.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Status & Tags (Separated for layout control) */}
          <div className="flex flex-col gap-2 mb-4">
            {/* Status Dropdown Container - Restricted Width (Col 5 equivalent) */}
            <div className="w-full md:w-5/12 relative status-dropdown">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Status
              </label>

              <div
                className="w-full text-sm py-2 px-3 border border-gray-300 rounded-md cursor-pointer bg-white"
                onClick={() => setIsStatusOpen(!isStatusOpen)}
              >
                <span className="text-gray-800">
                  {selectedStatuses.length > 0
                    ? `${selectedStatuses.length} selected`
                    : "— Select —"}
                </span>
              </div>

              {isStatusOpen && (
                <div
                  className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                  ref={statusRef}
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search status..."
                      value={statusSearch}
                      onChange={(e) => setStatusSearch(e.target.value)}
                      className="w-full text-sm py-1 px-2 border rounded outline-none"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div className="max-h-52 overflow-y-auto">
                    {filteredStatuses.map((status) => {
                      const isSelected = selectedStatuses.some(
                        (s) => s.value === status.value,
                      );

                      return (
                        <div
                          key={status.value}
                          className={`px-3 py-2 cursor-pointer text-sm ${
                            isSelected ? "bg-blue-100" : "hover:bg-gray-100"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();

                            if (isSelected) {
                              setSelectedStatuses(
                                selectedStatuses.filter(
                                  (s) => s.value !== status.value,
                                ),
                              );
                            } else {
                              setSelectedStatuses([
                                ...selectedStatuses,
                                status,
                              ]);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="me-2"
                          />
                          {status.title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Selected Status Tags - Now forced to new line below */}
            {selectedStatuses.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {selectedStatuses.map((status) => (
                  <div
                    key={status.value}
                    className="badge bg-primary d-flex align-items-center px-3 py-2"
                  >
                    <span className="me-2">{status.title}</span>
                    <button
                      className="btn-close btn-close-white btn-sm"
                      onClick={() =>
                        setSelectedStatuses(
                          selectedStatuses.filter(
                            (s) => s.value !== status.value,
                          ),
                        )
                      }
                    ></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions Button */}
          <div className="flex justify-end gap-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
            >
              Clear All
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-white hover:bg-blue-700 font-medium"
            >
              Apply Filters
            </button>
          </div>
        </div>
        {/* table data */}
        <div className="table-responsive ">
          <table className="table table-hover table-bordered border-light">
            <thead className="table-light">
              <tr>
                <th>Client Name</th>
                <th>Type</th>
                <th>Provider</th>
                <th>Course/Package</th>
                <th>Location</th>
                <th>Current Intake</th>
                <th>Current Status</th>
                <th>Milestone Progress</th>
                {/* <th>Intake Changes</th> */}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`}>
                    <td>
                      <div className="skeleton w-75 h-4 rounded" />
                    </td>
                    <td>
                      <div className="skeleton w-75 h-4 rounded" />
                    </td>
                    <td>
                      <div className="skeleton w-100 h-4 rounded" />
                    </td>
                    <td>
                      <div className="skeleton w-100 h-4 rounded" />
                    </td>
                    <td>
                      <div className="skeleton w-100 h-4 rounded" />
                    </td>
                    <td>
                      <div className="skeleton w-100 h-4 rounded" />
                    </td>
                    <td>
                      <div className="skeleton w-100 h-4 rounded" />
                    </td>
                    <td>
                      <div className="skeleton w-full h-4 rounded" />
                    </td>
                    {/* <td>
                      <div className="skeleton w-50 h-4 rounded mx-auto" />
                    </td> */}
                  </tr>
                ))
              ) : result.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-5 text-muted fst-italic"
                  >
                    {selectedIntakeId
                      ? "No applications found matching the current filters."
                      : "Please select an Intake to display applications."}
                  </td>
                </tr>
              ) : (
                result?.map((app) => (
                  <tr key={app.id}>
                    <td className="fw-medium align-middle">
                      <Link
                        to={`/clients/view-client-detail/${app?.student?.id}`}
                      >
                        {app?.student?.name || "-"}
                      </Link>
                    </td>
                    <td className="align-middle text-dark">
                      {app?.student?.type &&
                      app?.student?.type?.type === "B2B" ? (
                        <span className="badge bg-success-subtle text-success-emphasis px-2 py-1 border border-success-subtle">
                          B2B - {app?.student?.type?.agency_name}
                        </span>
                      ) : (
                        <span className="badge bg-light text-muted px-2 py-1 border">
                          B2C
                        </span>
                      )}
                    </td>
                    <td className="align-middle text-dark">
                      {app.provider || "—"}
                    </td>

                    <td className="align-middle text-dark">
                      {app.course_package || "—"}
                    </td>
                    <td className="align-middle text-dark">
                      {app.location || "—"}
                    </td>
                    <td className="align-middle text-dark fw-medium">
                      {app.intake || "—"}
                    </td>

                    <td className="align-middle">
                      <span className="badge bg-primary-subtle text-primary px-3 py-2 fw-normal">
                        {app.current_status?.title || "Unknown"}
                      </span>
                    </td>

                    <td className="align-middle">
                      <div className="d-flex flex-column gap-2 small text-dark">
                        <div>
                          <strong>Application Received:</strong>{" "}
                          {app.milestones?.application_received ? (
                            <span className="badge bg-success ms-2 px-2 py-1">
                              {new Date(
                                app.milestones.application_received,
                              ).toLocaleDateString("en-GB")}
                            </span>
                          ) : (
                            <span className="badge bg-light text-secondary border ms-2 px-2 py-1">
                              Pending
                            </span>
                          )}
                        </div>

                        <div>
                          <strong>Fee Paid (Offer Accepted):</strong>{" "}
                          {app.milestones?.offer_accepted_fee_paid ? (
                            <span className="badge bg-success ms-2 px-2 py-1">
                              {new Date(
                                app.milestones.offer_accepted_fee_paid,
                              ).toLocaleDateString("en-GB")}
                            </span>
                          ) : (
                            <span className="badge bg-light text-secondary border ms-2 px-2 py-1">
                              Pending
                            </span>
                          )}
                        </div>

                        <div>
                          <strong>Full Offer Received - GS Approved</strong>{" "}
                          {app.milestones?.full_offer_received_gs_approved ? (
                            <span className="badge bg-success ms-2 px-2 py-1">
                              {new Date(
                                app.milestones.full_offer_received_gs_approved,
                              ).toLocaleDateString("en-GB")}
                            </span>
                          ) : (
                            <span className="badge bg-light text-secondary border ms-2 px-2 py-1">
                              Pending
                            </span>
                          )}
                        </div>

                        <div>
                          <strong>COE/LOA Received:</strong>{" "}
                          {app.milestones
                            ?.offer_accepted_coe_cas_loa_received ? (
                            <span className="badge bg-success ms-2 px-2 py-1">
                              {new Date(
                                app.milestones
                                  .offer_accepted_coe_cas_loa_received,
                              ).toLocaleDateString("en-GB")}
                            </span>
                          ) : (
                            <span className="badge bg-light text-secondary border ms-2 px-2 py-1">
                              Pending
                            </span>
                          )}
                        </div>

                        <div>
                          <strong>Visa Submitted:</strong>{" "}
                          {app.milestones?.visa_submitted ? (
                            <span className="badge bg-success ms-2 px-2 py-1">
                              {new Date(
                                app.milestones.visa_submitted,
                              ).toLocaleDateString("en-GB")}
                            </span>
                          ) : (
                            <span className="badge bg-light text-secondary border ms-2 px-2 py-1">
                              Pending
                            </span>
                          )}
                        </div>

                        <div>
                          <strong>Visa Outcome:</strong>{" "}
                          {app.milestones?.visa_status ? (
                            <span
                              className={`badge ms-2 px-2 py-1 ${
                                app.milestones.visa_status.status === "Approved"
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {app.milestones.visa_status.status} —{" "}
                              {new Date(
                                app.milestones.visa_status.date,
                              ).toLocaleDateString("en-GB")}
                            </span>
                          ) : (
                            <span className="badge bg-light text-secondary border ms-2 px-2 py-1">
                              Pending
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* <td className="align-middle small text-center">
                      {app.intake_changed && app.intake_history?.length > 0 ? (
                        <div className="d-flex flex-column gap-1">
                          {app.intake_history.map((change, index) => (
                            <div key={index}>
                              {change.old_intake || "—"} →{" "}
                              {change.new_intake || "—"}
                              <small className="text-muted d-block">
                                {new Date(change.changed_at).toLocaleDateString(
                                  "en-GB",
                                )}
                              </small>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* pagination */}
        <div className="row align-items-center mb-5 mt-2 ">
          <div className="col-auto">
            <select
              value={pagination?.perPage}
              onChange={handlePerPageChange}
              className="form-select"
              style={{ minWidth: 80 }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
          <div className="col-auto text-muted">
            Page {pagination.currentPage} – Showing{" "}
            {Math.min(
              pagination.perPage,
              pagination.total -
                (pagination.currentPage - 1) * pagination.perPage,
            )}{" "}
            of {pagination.total} entries
          </div>
          <div className="col">
            <ReactPaginate
              previousLabel={"Previous"}
              nextLabel={"Next"}
              breakLabel={"..."}
              pageCount={pagination.lastPage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageChange}
              forcePage={pagination.currentPage - 1}
              containerClassName={"pagination justify-content-end mb-0"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              activeClassName={"active"}
              disabledClassName={"disabled"}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default ClientSignupData;
